import * as React from 'react';

// Guard: Ensure React is available at module evaluation time
if (!(React as any)?.Component) console.error('[RootErrorBoundary] React import is undefined in this bundle');

interface RootErrorBoundaryProps {
  children: React.ReactNode;
}

interface RootErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
}

/**
 * Bulletproof root error boundary with zero dependencies
 * Uses inline HTML to ensure it never crashes itself
 */
export class RootErrorBoundary extends React.Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
  constructor(props: RootErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): RootErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('🔴 RootErrorBoundary caught an error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });
    
    // Store error info in state for development display
    this.setState({ error, errorInfo } as any);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleClearData = () => {
    try {
      localStorage?.clear?.();
    } catch (e) {
      console.warn('Failed to clear localStorage:', e);
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: '#fef2f2',
          color: '#dc2626'
        }}>
          <h1 style={{ marginBottom: '16px', fontSize: '24px', fontWeight: 'bold' }}>
            We hit a snag
          </h1>
          <p style={{ marginBottom: '20px', textAlign: 'center', maxWidth: '500px' }}>
            The application encountered an unexpected error. Try reloading the page or clearing stored data.
          </p>
          {process.env.NODE_ENV === 'development' && (this.state as any).errorInfo && (
            <div style={{
              marginBottom: '20px',
              padding: '12px',
              background: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              maxWidth: '600px',
              textAlign: 'left'
            }}>
              <p style={{ fontWeight: 'bold', marginBottom: '8px' }}>Error Details (Dev Mode):</p>
              <p style={{ fontSize: '13px', marginBottom: '4px', wordBreak: 'break-word' }}>
                <strong>Message:</strong> {(this.state as any).error?.message}
              </p>
              <details style={{ fontSize: '12px', marginTop: '8px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Stack Trace</summary>
                <pre style={{ 
                  marginTop: '8px', 
                  padding: '8px', 
                  background: '#fff', 
                  overflow: 'auto', 
                  maxHeight: '200px',
                  fontSize: '11px'
                }}>
                  {(this.state as any).error?.stack}
                </pre>
              </details>
              <details style={{ fontSize: '12px', marginTop: '8px' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Component Stack</summary>
                <pre style={{ 
                  marginTop: '8px', 
                  padding: '8px', 
                  background: '#fff', 
                  overflow: 'auto', 
                  maxHeight: '200px',
                  fontSize: '11px',
                  whiteSpace: 'pre-wrap'
                }}>
                  {(this.state as any).errorInfo?.componentStack}
                </pre>
              </details>
            </div>
          )}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button 
              onClick={this.handleReload}
              style={{
                padding: '12px 24px',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Reload Page
            </button>
            <button 
              onClick={this.handleClearData}
              style={{
                padding: '12px 24px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              Clear Data & Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}